import { zodResolver } from '@hookform/resolvers/zod';
import {
  ForwardRefRenderFunction,
  RefObject,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { FormProvider, UseFormReturn, useForm } from 'react-hook-form';
import { FormActionButtons } from '../../../../common/Forms';
import { useDirtyFormBlockNavigation } from '../../../../common/Forms/NavigationBlocker';
import {
  HorizontalSeparator,
  Visible,
} from '../../../../common/LayoutComponents';
import { SheltersFormState, sheltersFormSchema } from './schema';
import { ShelterFormFields } from './ShelterFormFields';
import { useSheltersFormUtils } from './useSheltersForm';

const testIds = {
  shelter: 'SheltersForm::shelter',
  addShelter: 'SheltersForm::addShelter',
};

type SheltersFormProps = {
  readonly className?: string;
  readonly defaultValues: SheltersFormState;
  readonly formRef: RefObject<HTMLFormElement>;
  readonly onSubmit: (state: SheltersFormState) => void;
  readonly onShelterCountChanged: (newShelterCount: number) => void;
  readonly onCancel: () => void;
  readonly testIdPrefix: string;
  readonly addNewButton?: React.ReactNode;
};

export type SheltersFormRef = {
  readonly addNewShelter: () => void;
};

const SheltersFormComponent: ForwardRefRenderFunction<
  SheltersFormRef,
  SheltersFormProps
> = (
  {
    className,
    defaultValues,
    formRef,
    onSubmit,
    onShelterCountChanged,
    onCancel,
    testIdPrefix,
    addNewButton,
  },
  ref,
) => {
  const formElementRef = useRef<HTMLFormElement>(null);

  const methods: UseFormReturn<SheltersFormState> = useForm<SheltersFormState>({
    defaultValues,
    resolver: zodResolver(sheltersFormSchema),
  });
  useDirtyFormBlockNavigation(methods.formState, 'SheltersForm');

  const {
    shelters,
    addNewShelter,
    copyToNewShelter,
    onRemoveShelter,
    isLast,
    handleSubmit,
  } = useSheltersFormUtils({ methods, onShelterCountChanged });

  useImperativeHandle(ref, () => ({
    addNewShelter,
    submit: () => {
      if (formElementRef.current) {
        formElementRef.current.dispatchEvent(
          new Event('submit', { cancelable: true, bubbles: true }),
        );
      }
    },
  }));

  return (
    <FormProvider {...methods}>
      <form
        className={className}
        onSubmit={handleSubmit(onSubmit)}
        ref={formRef}
      >
        {shelters.map((shelter, idx) => (
          <div key={shelter.id} data-testid={testIds.shelter}>
            <ShelterFormFields
              index={idx}
              onRemove={onRemoveShelter}
              onCopy={copyToNewShelter}
            />
            <Visible visible={!isLast(idx)}>
              <HorizontalSeparator className="my-4" />
            </Visible>
          </div>
        ))}
        <FormActionButtons
          onCancel={onCancel}
          testIdPrefix={testIdPrefix}
          isDisabled={
            !methods.formState.isDirty || methods.formState.isSubmitting
          }
          isSubmitting={methods.formState.isSubmitting}
          addNewButton={addNewButton}
          variant="infoContainer"
        />
      </form>
    </FormProvider>
  );
};

export const SheltersForm = forwardRef<SheltersFormRef, SheltersFormProps>(
  SheltersFormComponent,
);
